import { makeStyles } from '@fluentui/react-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { NAV_ITEMS, isNavItemActive } from './navItems';

const useStyles = makeStyles({
  rail: {
    width: '72px',
    flexShrink: 0,
    backgroundColor: 'var(--hts-shell-bg)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '2px',
    paddingTop: '8px',
    paddingBottom: '8px',
    overflowY: 'auto',
    '@media (max-width: 767px)': {
      display: 'none', // mobile uses the header hamburger + drawer
    },
  },
  item: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    minHeight: '56px', // min tap target 64x56 per standard
    minWidth: '64px',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '4px',
    paddingRight: '4px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: 'rgba(255, 255, 255, 0.65)',
    transitionProperty: 'color, background-color',
    transitionDuration: '0.15s',
    ':hover': { color: '#FFFFFF' },
  },
  itemActive: {
    backgroundColor: 'rgba(0, 184, 235, 0.15)', // exact standard tint
    color: 'var(--hts-rail-active-ink)', // MC --color-accent (#E1F0FB) in both modes
    ':hover': { color: 'var(--hts-rail-active-ink)' },
  },
  indicator: {
    position: 'absolute',
    left: 0,
    top: '8px',
    bottom: '8px',
    width: '3px',
    borderTopRightRadius: '3px',
    borderBottomRightRadius: '3px',
    backgroundColor: 'var(--hts-nav-active-ink)',
  },
  icon: {
    fontSize: '22px',
    width: '22px',
    height: '22px',
    flexShrink: 0,
  },
  label: {
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.2,
    textAlign: 'center',
  },
});

/**
 * The fixed 72px primary rail (HTS app-shell standard): icon 22px + 11px
 * label per destination, navy in every mode; the active item gets the Action
 * Blue tint + 3px left indicator. Site Survey renders all items inline —
 * no flyout, no pin.
 */
export const NavRail: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <nav
      id="nav-rail"
      data-hts-nav-rail
      aria-label="Primary"
      className={styles.rail}
    >
      {NAV_ITEMS.map((item) => {
        const isActive = isNavItemActive(location.pathname, item.path);
        const Icon = item.icon;
        return (
          <button
            key={item.path}
            type="button"
            data-testid={`nav-rail-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            aria-current={isActive ? 'page' : undefined}
            className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
            onClick={() => navigate(item.path)}
          >
            {isActive && <span className={styles.indicator} />}
            <Icon className={styles.icon} />
            <span className={styles.label}>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default NavRail;
